import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { PlayerNotificationList } from "../components/PlayerNotificationList";

/**
 * An `action_url` is safe to hand react-router only if it is an IN-APP, rooted,
 * same-origin relative path. react-router does NOT neutralize an absolute /
 * scheme-bearing / protocol-relative URL — navigate("https://evil.com") falls
 * through pushState(SecurityError) to window.location.assign(), a real off-site
 * navigation (open-redirect / phishing pivot). So the wiring NEVER hands navigate
 * anything but a verified in-app path; everything else is ignored.
 *
 * The origin check (via `new URL`) is the robust form: it rejects not just
 * obvious "https://"/"//host"/"javascript:" but also obfuscated cases like a
 * tab-prefixed "\t/\\evil.com" that a naive leading-slash regex would let through
 * after trimming.
 */
function isInAppRelativePath(to: string): boolean {
  const raw = to.trim();
  // Reject absolute / scheme-bearing / protocol-relative (mirrors react-router's
  // own ABSOLUTE_URL_REGEX).
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(raw)) return false;
  if (!raw.startsWith("/")) return false; // must be a rooted in-app path
  try {
    // Resolve against a sentinel origin: anything that escapes it is off-site.
    return new URL(raw, "http://localhost").origin === "http://localhost";
  } catch {
    return false;
  }
}

/**
 * NotificationsPage — the player's notification inbox.
 *
 * Thin page wrapper (mirrors MePage): PlayerNotificationList reads the live inbox
 * via notificationApi.list and renders the honest read-model with native MUI.
 * This page supplies the L2 width-cap frame and the guarded navigation callback.
 *
 * The list is router-agnostic, so the page supplies navigation: an
 * actionable notification carrying `action_url` (e.g. a DM → its thread) deep-
 * links via react-router's navigate when clicked — but ONLY for a verified
 * in-app relative path (defense-in-depth open-redirect guard, see above).
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const onNavigate = (url: string) => {
    if (isInAppRelativePath(url)) navigate(url);
  };
  return (
    <Page title="Notifications" narrow>
      <Box component="section" aria-label="Notification inbox">
        <PlayerNotificationList onNavigate={onNavigate} />
      </Box>
    </Page>
  );
}
